import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Target, Heart, Handshake, ArrowRight, Droplets } from 'lucide-react';

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const pillars = [
  { icon: Target, title: "Attentif, à l'écoute de vos besoins", desc: "Ensemble, nous prenons le temps de comprendre, d'évaluer et de chiffrer vos différents besoins." },
  { icon: Heart, title: "Consciencieux et passionné", desc: "Après plus de 20 ans au service notamment des collectivités, j'ai à cœur de réaliser un travail de qualité, dans l'objectif de toujours vous satisfaire." },
  { icon: Handshake, title: "Expérimenté et disponible", desc: "Quelle que soit la nature de votre besoin ou de votre projet (reportages, prises de vues ou conférences) je vous proposerai une solution personnalisée qui saura répondre à vos attentes les plus exigeantes." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[40vh] overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/prq4obov__DSC4113.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D15] via-[#0B1D15]/60 to-[#0B1D15]/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 z-10 px-6">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-playfair text-5xl md:text-6xl text-white text-center tracking-tight mb-3">
            Qui suis-je ?
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="font-lato text-lg text-[#8CA1B0] text-center">
            Un passionné au cœur des eaux
          </motion.p>
        </div>
      </section>

      {/* Bio principale */}
      <section className="py-20 bg-gradient-to-b from-[#0F2318] to-[#0B1D15] relative overflow-hidden">
        <div className="caustics-overlay" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <FadeIn>
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              <div className="lg:w-[45%] flex-shrink-0">
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/ns66x7p5_1%20-%20Auteur.jpg"
                    alt="Yannick Gouguenheim"
                    className="w-full h-auto object-cover rounded-2xl"
                  />
                </div>
              </div>
              <div className="lg:w-[55%]">
                <div className="flex items-center gap-3 mb-6">
                  <Droplets size={20} className="text-[#2B7BA8]" />
                  <h2 className="font-montserrat text-lg font-semibold text-white">Pourquoi avoir choisi ce métier ?</h2>
                </div>
                <div className="space-y-5 font-lato text-[#8CA1B0] leading-relaxed">
                  <p>Tout a commencé, enfant, sur les bords de la petite mare de mon village.</p>
                  <p>Intrigué par ces eaux mystérieuses, j'observais des heures durant la vie foisonnante de ce petit trou d'eau. Rainettes, crapauds, tritons, têtards et libellules faisaient mon bonheur.</p>
                  <p>Les souvenirs de ces moments aussi simples qu'intenses sont à l'origine d'une passion qui ne m'a jamais plus quitté : celle de la vie des eaux douces.</p>
                  <p>Naturellement, j'ai suivi des études universitaires en biologie et en gestion des espaces naturels.</p>
                  <p>Chargé de la communication et formateur en écologie une douzaine d'années dans différentes Fédérations pour la Pêche et la Protection des Milieux Aquatiques, j'ai pu approfondir mes connaissances.</p>
                  <p>Plongeur aguerri, la photographie en eau douce est devenue une évidence. En 2009, je commençais à réaliser mes premières images subaquatiques.</p>
                  <p>Par ailleurs, je réalise, pour les acteurs de l'eau, des reportages photographiques ainsi que des films de sensibilisation et de valorisation de leurs espaces naturels.</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Double constat */}
      <section className="py-16 section-stone relative overflow-hidden">
        <div className="caustics-overlay" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <FadeIn>
            <h2 className="font-playfair text-2xl md:text-3xl text-white mb-8">
              Ce désir de me diriger vers l'image subaquatique venait de deux constats :
            </h2>
            <div className="w-12 h-0.5 bg-[#2B7BA8] mb-8" />
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="w-3 h-3 rounded-full bg-[#2B7BA8] mt-2 flex-shrink-0" />
                <p className="font-lato text-[#8CA1B0] leading-relaxed">
                  J'ai pris conscience du manque de supports visuels subaquatiques, ce qui limitait la communication des gestionnaires des milieux aquatiques. Mon objectif était de les soutenir dans leur communication en leur fournissant des images de la biodiversité, des paysages immergés et de mettre en valeur le patrimoine naturel de leurs bassins versants.
                </p>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-3 h-3 rounded-full bg-[#2B7BA8] mt-2 flex-shrink-0" />
                <p className="font-lato text-[#8CA1B0] leading-relaxed">
                  Second constat, les enfants et plus généralement le grand public connaissaient mieux la grande faune africaine que les espèces animales de nos régions, de nos rivières. Ainsi, j'ai souhaité utiliser mes compétences en environnement et les images que je produisais pour participer à sensibiliser et communiquer sur nos problématiques liées aux milieux aquatiques continentaux.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Une démarche rigoureuse */}
      <section className="py-20 section-emerald relative overflow-hidden">
        <div className="caustics-strong" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <FadeIn className="mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl text-white">Une démarche rigoureuse</h2>
            <div className="w-12 h-0.5 bg-[#C4A35A] mt-4" />
          </FadeIn>
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2 space-y-8">
              {pillars.map((p, i) => (
                <FadeIn key={i} delay={i * 0.15}>
                  <div className="flex gap-5 items-start">
                    <div className="w-12 h-12 rounded-xl bg-[#1A5C4F]/25 flex items-center justify-center flex-shrink-0">
                      <p.icon size={22} className="text-[#3A7D5C]" />
                    </div>
                    <div>
                      <h3 className="font-montserrat text-base font-semibold text-white mb-2">{p.title}</h3>
                      <p className="font-lato text-sm text-[#8CA1B0] leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.2} className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/30">
                <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/ay7sfonr__DSC7050.jpg" alt="Grenouille aux aguets" className="w-full h-[400px] lg:h-[450px] object-cover" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 cta-parallax relative overflow-hidden"
        style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_template-astud/artifacts/n2bfgxlk__DSC8423%20%281%29.jpg')` }}
      >
        <div className="caustics-overlay" style={{ zIndex: 2 }} />
        <FadeIn className="max-w-3xl mx-auto text-center px-6 relative z-10">
          <h2 className="font-playfair text-3xl md:text-4xl text-white mb-8">Envie de collaborer ?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prestations" data-testid="about-cta-prestations" className="px-8 py-3.5 rounded-full bg-[#C4A35A] text-[#0B1D15] font-montserrat text-sm uppercase tracking-wider hover:bg-[#D4B76A] transition-all duration-300 font-semibold">
              Découvrir mes prestations
            </Link>
            <Link to="/contact" data-testid="about-cta-contact" className="px-8 py-3.5 rounded-full border-2 border-white/30 text-white font-montserrat text-sm uppercase tracking-wider hover:bg-white/10 transition-all duration-300 inline-flex items-center gap-2">
              Me contacter <ArrowRight size={14} />
            </Link>
          </div>
        </FadeIn>
      </section>
    </main>
  );
}
